import "zod/compile";
import { z } from "zod";

/**
 * The OMS metric catalog — the "tool contract as ontology" layer. Every
 * dimension a sales-order question can vary on is an enum with a description
 * the model reads, so "total sales last week" is resolved to one of these
 * choices instead of a guess. The same descriptions feed the glossary.
 *
 * Every population and measure is defined so that a number quoted in chat
 * matches what the sales-orders page and its CSV export show.
 */
export const OMS_MEASURES = {
  order_count:
    "Number of order drafts (one per draft row; a split PO counts once per sibling — see po_count).",
  po_count:
    "Number of distinct customer purchase orders: split siblings collapse onto their split group.",
  total_amount:
    "Sum of order total amounts in the order's own currency; mixed currencies are reported per currency, never summed together. Rows with a blank amount are counted in order_count but excluded from the sum (see blankAmountCount).",
  line_count:
    "Number of order lines across the selected orders (the page's Items column).",
  avg_order_value: "Average order total amount per order, per currency.",
} as const;
export const OmsMeasureSchema = z.enum(
  Object.keys(OMS_MEASURES) as [
    keyof typeof OMS_MEASURES,
    ...Array<keyof typeof OMS_MEASURES>,
  ],
);
export type OmsMeasure = z.infer<typeof OmsMeasureSchema>;

export const OMS_DATE_BASES = {
  received_date:
    "When the customer's purchase order arrived (email received / EDI received). The default for 'recent orders' and for sales-volume windows.",
  order_date: "The order date printed on the customer's purchase order.",
  created_at: "When the draft record was created in the ERP.",
} as const;
export const OmsDateBasisSchema = z.enum(
  Object.keys(OMS_DATE_BASES) as [
    keyof typeof OMS_DATE_BASES,
    ...Array<keyof typeof OMS_DATE_BASES>,
  ],
);
export type OmsDateBasis = z.infer<typeof OmsDateBasisSchema>;

export const OMS_GROUP_BYS = {
  customer: "One row per customer.",
  stage:
    "One row per lifecycle stage as the page's Status column shows it (Draft, Resolving Discounts, Approving, Ready, Submitted, Rejected, Superseded).",
  status:
    "One row per raw draft status (RECEIVED, APPROVED, REJECTED, ...). Prefer stage.",
  day: "One row per calendar day of the chosen date basis.",
  week: "One row per ISO week of the chosen date basis.",
  currency: "One row per currency.",
} as const;
export const OmsGroupBySchema = z.enum(
  Object.keys(OMS_GROUP_BYS) as [
    keyof typeof OMS_GROUP_BYS,
    ...Array<keyof typeof OMS_GROUP_BYS>,
  ],
);
export type OmsGroupBy = z.infer<typeof OmsGroupBySchema>;

export const OMS_POPULATIONS = {
  all: "Every order draft the sales-orders page lists by default: any status, superseded revisions hidden. Use for 'total orders received'.",
  submitted_to_erp:
    "Orders that reached the ERP: an ERP adapter created a sales order, or a record-path submission called an ERP API. Excludes 'approved without ERP' (reported separately as approvedWithoutErp). Use for 'sales volume' / 'submitted to Oracle'. Some may still be waiting for an ERP order number (see erpPending).",
  submitted_any:
    "Everything the page badges as Submitted, including orders approved without any ERP call.",
  open: "Drafts still in progress (page stages Draft, Resolving Discounts, Approving, Ready): not yet submitted, rejected or superseded.",
  rejected: "Drafts the page badges as Rejected (not superseded).",
  superseded:
    "Drafts replaced by a revised PO; hidden from every other population.",
} as const;
export const OmsPopulationSchema = z.enum(
  Object.keys(OMS_POPULATIONS) as [
    keyof typeof OMS_POPULATIONS,
    ...Array<keyof typeof OMS_POPULATIONS>,
  ],
);
export type OmsPopulation = z.infer<typeof OmsPopulationSchema>;

/** Lifecycle stages a filter may name (the page's Status column vocabulary). */
export const OmsStageSchema = z.enum([
  "draft",
  "creating_discounts",
  "approving",
  "ready",
  "created",
  "under_cs_review",
  "rejected",
  "superseded",
]);
export type OmsStage = z.infer<typeof OmsStageSchema>;

/** One line of a `sales_order_metrics` result. */
export const OmsMetricRowSchema = z.object({
  groupKey: z.string().nullable(),
  groupLabel: z.string().nullable(),
  currency: z.string().nullable(),
  value: z.number(),
  orderCount: z.number().int(),
});
export type OmsMetricRow = z.infer<typeof OmsMetricRowSchema>;

/** Numbers that qualify a metric answer so it reconciles with the analyst's spreadsheet method. */
export const OmsMetricCompanionsSchema = z.object({
  /** Submitted to the ERP but the ERP order number has not come back yet. */
  erpPending: z.object({
    orderCount: z.number().int(),
    byCurrency: z.array(
      z.object({ currency: z.string().nullable(), totalAmount: z.number() }),
    ),
  }),
  /** Badged Submitted with no ERP call; excluded from submitted_to_erp. */
  approvedWithoutErp: z.object({ orderCount: z.number().int() }),
  /** Rows in the population with a blank total amount (counted, not summed). */
  blankAmountCount: z.number().int(),
  /**
   * The spreadsheet figure: every row in the population summed as entered,
   * regardless of currency. Many orders carry no currency, so quote this only
   * next to the per-currency rows and with `blankCurrencyCount`.
   */
  grandTotal: z.object({
    orderCount: z.number().int(),
    totalAmount: z.number(),
    blankCurrencyCount: z.number().int(),
  }),
  /** True when superseded revisions were hidden (every population except `superseded`). */
  supersededHidden: z.boolean(),
});
export type OmsMetricCompanions = z.infer<typeof OmsMetricCompanionsSchema>;

export function describeEnum(map: Record<string, string>): string {
  return Object.entries(map)
    .map(([k, v]) => `${k}: ${v}`)
    .join(" | ");
}
